import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiBase } from '../utils/authHeaders';

const receiverHistory = item => (Array.isArray(item.reconciliation_history) ? item.reconciliation_history : [])
  .filter(proof => proof && proof.event_id === item.event_id && proof.content_hash === item.content_hash &&
    ['RECEIVER_ACCEPTED', 'RECEIVER_NOT_FOUND'].includes(proof.status) &&
    proof.provider_effect === 'NOT_ASSESSED' && proof.no_action_authority === true).slice(-10);

export default function OperandiDeliveryStatus({ headers }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState({});
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const load = async () => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setBusy(true); setError(''); setReport(null);
    try {
      const response = await fetch(`${apiBase()}/api/operandi/delivery`, { headers, signal: request.signal });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Delivery diagnostics unavailable');
      if (!result.counts || !Array.isArray(result.blocked_items) || result.no_action_authority !== true || result.provider_health !== 'NOT_ASSESSED')
        throw new Error('Invalid delivery diagnostics');
      if (!request.signal.aborted) setReport(result);
    } catch (failure) { if (!request.signal.aborted) setError(failure.message); }
    finally { if (!request.signal.aborted) setBusy(false); }
  };
  const reconcile = async item => {
    if (busy || !confirmed[item.event_id]) return;
    const request = new AbortController(); controller.current = request;
    setBusy(true); setError('');
    try {
      const response = await fetch(`${apiBase()}/api/operandi/delivery/${encodeURIComponent(item.event_id)}/reconcile`, {
        method:'POST', headers:{...headers,'Content-Type':'application/json'}, signal:request.signal,
        body:JSON.stringify({expected_content_hash:item.content_hash,authorize_readback:true}) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Receiver read-back unavailable; event not requeued');
      if (result.event_id !== item.event_id || result.content_hash !== item.content_hash ||
          !['RECEIVER_ACCEPTED','RECEIVER_NOT_FOUND'].includes(result.status) ||
          result.provider_effect !== 'NOT_ASSESSED' || result.no_action_authority !== true)
        throw new Error('Invalid receiver read-back; event not requeued');
      if (!request.signal.aborted) setReport(current => ({...current,blocked_items:current.blocked_items.map(row =>
        row.event_id === item.event_id ? {...row,reconciliation:result,
          reconciliation_history:[...receiverHistory(row),result].slice(-10)} : row)}));
    } catch (failure) { if (!request.signal.aborted) setError(failure.message); }
    finally { if (!request.signal.aborted) setBusy(false); }
  };
  return <section className="mt-5 border-t border-gray-200 pt-4" aria-label="Operandi event delivery">
    <h2 className="font-semibold">Event delivery</h2>
    <button type="button" disabled={busy} onClick={load} className="mt-2 inline-flex min-h-11 items-center gap-2 rounded border border-gray-300 px-3"><RefreshCw size={16} />{busy ? 'Loading delivery' : 'Check delivery'}</button>
    {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
    {report && <div className="mt-3 space-y-2 text-sm">
      <p role="status">Delivery review: {report.status}. Dispatch: {report.dispatch_enabled ? 'enabled' : 'disabled'}; site binding: {report.binding_enabled ? 'enabled' : 'disabled'}.</p>
      <p>Provider health: NOT_ASSESSED. Observed at: {report.as_of}.</p>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5">{Object.entries(report.counts).map(([state, count]) => <div key={state}><dt>{state}</dt><dd className="font-semibold">{count}</dd></div>)}</dl>
      {!!report.unknown_state_count && <p role="alert">Unrecognized delivery states: {report.unknown_state_count}.</p>}
      <ul className="divide-y divide-gray-200">{report.blocked_items.map(item => <li key={item.event_id} className="py-2 break-words">
        <p className="font-medium">{item.state}: {item.event_id}</p>
        <p>{item.event_type || 'Event type not recorded'}; attempts: {item.attempts ?? 'Not recorded'}.</p>
        <p>Reason: {item.reason}{item.http_status ? `; HTTP ${item.http_status}` : ''}.</p>
        {item.reconciliation && <p role="status">Receiver read-back: {item.reconciliation.status}. Event remains {item.state}; provider effect: NOT_ASSESSED.</p>}
        {receiverHistory(item).length > 0 && <details className="mt-2">
          <summary className="cursor-pointer min-h-11 py-2">Recent receiver checks ({receiverHistory(item).length})</summary>
          <ol className="space-y-2">{receiverHistory(item).map((proof, index) => <li key={index}>
            <p>{proof.status}; checked at: {proof.checked_at || 'Not recorded'}; checked by: {proof.checked_by_user_id || 'Not recorded'}.</p>
            <p>Provider effect: NOT_ASSESSED.</p>
          </li>)}</ol>
        </details>}
        {typeof item.content_hash === 'string' && /^[a-f0-9]{64}$/.test(item.content_hash) && <div className="mt-2 space-y-2">
          <label className="flex items-center gap-2"><input type="checkbox" disabled={busy} checked={!!confirmed[item.event_id]} onChange={event => setConfirmed(current => ({...current,[item.event_id]:event.target.checked}))} />Authorize receiver read-back for {item.event_id}</label>
          <button type="button" disabled={busy || !confirmed[item.event_id]} onClick={() => reconcile(item)} className="inline-flex min-h-11 items-center gap-2 rounded border border-gray-300 px-3"><RefreshCw size={16}/>Check receiver</button>
        </div>}
      </li>)}</ul>
      {report.blocked_items_truncated && <p>Showing the first 50 blocked events.</p>}
    </div>}
  </section>;
}
